import { Link } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Thumb } from '@/components/Thumb/Thumb';
import { WishButton } from '@/components/WishButton/WishButton';
import { useAddToCart } from '@/hooks/useAddToCart';
import { priceLabel } from '@/utils/format';
import { paths } from '@/routes/paths';
import styles from './ProductCard.module.css';

export function ProductCard({ product, showCategory = false }) {
  const addToCart = useAddToCart();

  return (
    <article className={styles.card}>
      <div className={styles.media}>
        {/* Redundant with the title link below — hidden from the a11y tree so screen
            readers don't announce the same destination twice. */}
        <Link
          to={paths.product(product.id)}
          className={styles.thumbLink}
          aria-hidden="true"
          tabIndex={-1}
        >
          <Thumb
            letter={product.letter}
            tint={product.tint}
            deep={product.deep}
            src={product.image_url}
            size="md"
          />
        </Link>
        <WishButton productId={product.id} productName={product.name} floating />
      </div>

      <div className={styles.body}>
        {showCategory && <div className={styles.eyebrow}>{product.categoryName}</div>}

        <Link to={paths.product(product.id)} className={styles.name}>
          {product.name}
        </Link>

        {product.hasRating ? (
          <div className={styles.rating}>
            <span aria-hidden="true">{product.stars}</span>
            <span className={styles.ratingCount}> ({product.ratingCount})</span>
            <span className="sr-only">
              Rated {product.rating} out of 5 from {product.ratingCount} ratings
            </span>
          </div>
        ) : (
          <div className={styles.meta}>
            {product.author && <span>{product.author}</span>}
            {product.publisher?.name && <span> · {product.publisher.name}</span>}
          </div>
        )}

        <div className={styles.langs}>
          {/* Digital vs printed changes what buying this actually gets you, so
              it belongs on the card rather than only on the product page. */}
          <span className={styles.format}>
            {product.format === 'printed' ? 'Printed' : 'Digital'}
          </span>
          {product.language && (
            <>
              {' · '}
              {product.language}
              {product.additional_languages?.length > 0 &&
                ` +${product.additional_languages.length} more`}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.price}>{priceLabel(product)}</span>
          <Button variant="yellow" size="sm" onClick={() => addToCart(product)}>
            Add
          </Button>
        </div>
      </div>
    </article>
  );
}
